import './App.css';
import API_BASE from './apiConfig';
import './styles/responsive.css';
import { createBrowserRouter, RouterProvider, Outlet, useNavigate } from "react-router-dom";
import ClientState from './context/clients/ClientState';
import { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import Dashboard from './components/Dashboard/Dashboard';
import InvoiceState from './context/invoices/InvoiceState';
import UserState from './context/user/UserState';
import userContext from './context/user/userContext';
import BankState from './context/bank/BankState';
import LandingPage from './components/LandingPage/LandingPage';
import PrivacyPolicy from './components/LandingPage/PrivacyPolicy';
import TermsOfService from './components/LandingPage/TermsOfService';

// ── SuperAdmin components ──────────────────────────────

// ── New auth pages ─────────────────────────────────────


// ── v2 design (opt-in; v1 remains the default) ────────
import V2Home from './v2/V2Home';
import V2Clients from './v2/V2Clients';
import V2Portal from './v2/V2Portal';
import V2Invoices from './v2/V2Invoices';
import V2CreateInvoice from './v2/V2CreateInvoice';
import V2Ledger from './v2/V2Ledger';
import V2TDS from './v2/V2TDS';
import V2CreditNotes from './v2/V2CreditNotes';
import V2Recurring from './v2/V2Recurring';
import V2Bills from './v2/V2Bills';
import V2PurchaseOrders from './v2/V2PurchaseOrders';
import V2Purchases from './v2/V2Purchases';
import V2DebitNotes from './v2/V2DebitNotes';
import V2Suppliers from './v2/V2Suppliers';
import V2SupplierLedger from './v2/V2SupplierLedger';
import V2Stock from './v2/V2Stock';
import V2Import from './v2/V2Import';
import { V2Staff, V2Attendance, V2Payroll } from './v2/V2People';
import { V2Salespeople, V2SalespersonProfile, V2Commissions } from './v2/V2Sales';
import { V2BusinessReport, V2Financials, V2GSTReports } from './v2/V2Reports';
import V2Expenses from './v2/V2Expenses';
import { V2Profile, V2Bank, V2Team } from './v2/V2Account';
import V2Onboarding from './v2/V2Onboarding';
import V2Toast from './v2/V2Toast';
import { V2InvoiceRedirect, v2ProductRedirect, V2HomeRedirect, V2Loading, adminFramed } from './v2/V2Routes';
import V2Notifications from './v2/V2Notifications';
import { V2Login, V2Register, V2Forgot, V2AcceptInvite, V2GoogleCallback, V2AccessDenied } from './v2/V2Auth';
import V2PublicInvoice from './v2/V2PublicInvoice';
import { V2SAOverview, V2SAEntities, V2SAEntity } from './v2/V2SuperAdmin';

import { ThemeProvider, CssBaseline } from '@mui/material';
import theme from './theme';
import 'bootstrap/dist/css/bootstrap.min.css';

// ── v1/v2 switch for pages that have a v2 design ──────
const ClientsPage = (props) => <V2Clients {...props} />;
const InvoicesPage = (props) => <V2Invoices {...props} />;
const CreateInvoicePage = (props) => <V2CreateInvoice {...props} />;
const LedgerRoutePage = (props) => <V2Ledger {...props} />;
const TDSPage = (props) => <V2TDS {...props} />;
const CreditNotesPage = (props) => <V2CreditNotes {...props} />;
const RecurringPage = (props) => <V2Recurring {...props} />;
const BillsRoutePage = (props) => <V2Bills {...props} />;
const POPage = (props) => <V2PurchaseOrders {...props} />;
const PurchaseEntryPage = (props) => <V2Purchases {...props} />;
const DebitNotesPage = (props) => <V2DebitNotes {...props} />;
const SuppliersPage = (props) => <V2Suppliers {...props} />;
const SupplierLedgerPage = (props) => <V2SupplierLedger {...props} />;
const StockPage = (props) => <V2Stock {...props} />;
const ImportPage = (props) => <V2Import {...props} />;
const StaffRoutePage = (props) => <V2Staff {...props} />;
const AttendanceRoutePage = (props) => <V2Attendance {...props} />;
const PayrollRoutePage = (props) => <V2Payroll {...props} />;
const SalespeoplePage = (props) => <V2Salespeople {...props} />;
const SalespersonProfilePage = (props) => <V2SalespersonProfile {...props} />;
const CommissionsPage = (props) => <V2Commissions {...props} />;
const ReportsPage = (props) => <V2BusinessReport {...props} />;
const FinancialReportsPage = (props) => <V2Financials {...props} />;
const GSTReportsPage = (props) => <V2GSTReports {...props} />;
const ExpensesPage = (props) => <V2Expenses {...props} />;
const ProfilePage = (props) => <V2Profile {...props} />;
const BankPage = (props) => <V2Bank {...props} />;
const TeamPage = (props) => <V2Team {...props} />;
const OnboardingPage = (props) => <V2Onboarding {...props} />;
// Leftover v1 routes → v2 stand-ins (v1 unchanged)
const AccountsPage = (props) => <V2HomeRedirect {...props} />;
const SingleInvoicePage = (props) => <V2InvoiceRedirect {...props} />;
const V2ProductEdit = v2ProductRedirect("edit"), V2ProductAdjust = v2ProductRedirect("adjust"), V2ProductHistory = v2ProductRedirect("history");
const EditProductPage = (props) => <V2ProductEdit {...props} />;
const StockAdjustPage = (props) => <V2ProductAdjust {...props} />;
const StockHistoryPage = (props) => <V2ProductHistory {...props} />;
// Notifications: inside the company app (/dashboard) and inside the super admin console
const NotificationsRoutePage = (props) => <V2Notifications {...props} />;
// Sign-in & account screens (v2 only for browsers that opted in until v2 becomes the default)
const LoginPage = (props) => <V2Login {...props} />;
const RegisterPage = (props) => <V2Register {...props} />;
const ForgotPage = (props) => <V2Forgot {...props} />;
const AcceptInvitePage = (props) => <V2AcceptInvite {...props} />;
const GoogleCallbackPage = (props) => <V2GoogleCallback {...props} />;
const AccessDeniedPage = (props) => <V2AccessDenied {...props} />;
const PublicInvoicePage = (props) => <V2PublicInvoice {...props} />;
const V2SAOverviewFramed = adminFramed(V2SAOverview), V2SAEntitiesFramed = adminFramed(V2SAEntities);
const V2SAEntityFramed = adminFramed(V2SAEntity), V2SAMessagesFramed = adminFramed(V2Notifications);
const SADashboardPage = (props) => <V2SAOverviewFramed {...props} />;
const SAEntitiesPage = (props) => <V2SAEntitiesFramed {...props} />;
const SAEntityPage = (props) => <V2SAEntityFramed {...props} />;
const SAMessagesPage = (props) => <V2SAMessagesFramed {...props} />;

// ── Route → module map for guards ─────────────────────
const ROUTE_GUARD_MAP = {
  "manageclients": "clients",
  "createinvoice": "invoices",
  "viewinvoices": "invoices",
  "ledgerpage": "ledger",
  "expenses": "expenses",
  "managestock": "stock",
  "reports": "reports",
  "importproducts": "stock",
  "sales/salespeople": "sales",
  "sales/commissions": "sales",
  "tdsreceivable": "tds",
  "gst-reports": "gst",
  "purchase-orders": "purchases",
  "purchase-entry": "purchases",
  "managesuppliers": "suppliers",
  "financial-reports": "reports",
  "staff": "staff",
  "staff/attendance": "attendance",
  "staff/payroll": "payroll",
  "bills": "bills",
  "team": "team",
  "credit-notes": "invoices",
  "debit-notes": "purchases",
  "supplier-ledger": "suppliers",
};

// ── Route guard — permission-based ─────────────────────
function RouteGuard({ children, module }) {
  const { user, hasPermission, isAdmin } = useContext(userContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    if (module === "team") {
      if (!isAdmin()) navigate("/403", { replace: true });
      return;
    }
    if (module && !hasPermission(module, "read")) {
      navigate("/403", { replace: true });
    }
  }, [user, module]);

  return children;
}

// ── SuperAdmin guard — role-based ──────────────────────
function SuperAdminGuard({ children }) {
  const { user } = useContext(userContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    if (user.role !== "superadmin") {
      navigate("/403", { replace: true });
    }
  }, [user]);

  return children;
}

// ── Layout ─────────────────────────────────────────────
// The alert banner is rendered by App, outside the router (see App below).
function Layout() {
  return (
    <>
      <div className="app-content">
        <Outlet />
      </div>
    </>
  );
}

// ── OnboardingGate ─────────────────────────────────────
function OnboardingGate({ showAlert }) {
  const [status, setStatus] = useState(null);
  const navigate = useNavigate();

  // target: optional page to land on once onboarding is complete (v2 "what next" cards). v1 calls it with no args.
  const checkStatus = (target) => {
    const token = localStorage.getItem("token");
    if (!token) { navigate("/login", { replace: true }); return; }

    fetch(`${API_BASE}/api/auth/getuser`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "auth-token": token },
    })
      .then(r => r.json())
      .then(data => {
        if (data.role && data.role !== "owner") {
          setStatus(true);
          navigate("/dashboard/home", { replace: true });
          return;
        }
        if (data.onboardingComplete) {
          setStatus(true);
          navigate(typeof target === "string" ? target : "/dashboard/home", { replace: true });
        } else {
          setStatus(false);
        }
      })
      .catch(() => navigate("/login", { replace: true }));
  };

  useEffect(() => { checkStatus(); }, []);

  if (status === null) return <V2Loading />;

  if (status === false) return <OnboardingPage showAlert={showAlert} onComplete={checkStatus} />;
  return <Dashboard showAlert={showAlert} />;
}

// ── Guarded route helper ───────────────────────────────
const guarded = (module, Component, props) => (
  <RouteGuard module={module}>
    <Component {...props} />
  </RouteGuard>
);

// ── SuperAdmin guarded route helper ───────────────────
const superAdminGuarded = (Component, props) => (
  <SuperAdminGuard>
    <Component {...props} />
  </SuperAdminGuard>
);

// ══════════════════════════════════════════════════════
// APP
// ══════════════════════════════════════════════════════
function App() {
  const [alert, setAlert] = useState(null);

  // Stable, and the router below is created once: previously the router was rebuilt on every
  // render, so each alert (show + clear) remounted the whole page — it looked like a refresh.
  const showAlert = useCallback((message, type) => {
    setAlert({ msg: message, type });
    setTimeout(() => setAlert(null), 1500);
  }, []);

  const myRouter = useMemo(() => createBrowserRouter([
    {
      Component: Layout,
      children: [
        { path: "", Component: () => <LandingPage showAlert={showAlert} /> },
        { path: "/login", Component: () => <LoginPage showAlert={showAlert} /> },
        { path: "/register", Component: () => <RegisterPage showAlert={showAlert} /> },
        { path: "/auth/callback", Component: () => <GoogleCallbackPage /> },
        { path: "/privacy", Component: () => <PrivacyPolicy showAlert={showAlert} /> },
        { path: "/terms", Component: () => <TermsOfService showAlert={showAlert} /> },

        { path: "/accept-invite/:token", Component: () => <AcceptInvitePage showAlert={showAlert} /> },
        { path: "/403", Component: () => <AccessDeniedPage /> },
        { path: "/forgot-password", Component: () => <ForgotPage /> },
        { path: "/invoice/:publicId", Component: () => <PublicInvoicePage /> },
        { path: "/portal/:token", Component: () => <V2Portal /> },

        // ── SuperAdmin routes ──────────────────────
        {
          path: "/superadmin",
          Component: () => superAdminGuarded(SADashboardPage, { showAlert }),
        },
        {
          path: "/superadmin/entities",
          Component: () => superAdminGuarded(SAEntitiesPage, { showAlert }),
        },
        {
          path: "/superadmin/entities/:id",
          Component: () => superAdminGuarded(SAEntityPage, { showAlert }),
        },
        {
          path: "/superadmin/notifications",
          Component: () => superAdminGuarded(SAMessagesPage, { showAlert }),
        },

        // ── Dashboard ─────────────────────────────
        {
          path: "/dashboard",
          Component: () => <OnboardingGate showAlert={showAlert} />,
          children: [
            { path: "home", Component: () => <V2Home showAlert={showAlert} /> },
            { path: "profile", Component: () => <ProfilePage showAlert={showAlert} /> },
            { path: "addbankdetails", Component: () => <BankPage showAlert={showAlert} /> },
            { path: "accounts", Component: () => <AccountsPage showAlert={showAlert} /> },
            { path: "invoice/:id", Component: () => <SingleInvoicePage showAlert={showAlert} /> },

            // Notifications — entity users
            { path: "notifications", Component: () => <NotificationsRoutePage showAlert={showAlert} /> },

            // ── Guarded routes ─────────────────────
            { path: "manageclients",     Component: () => guarded("clients",    ClientsPage,      { showAlert }) },
            { path: "createinvoice",     Component: () => guarded("invoices",   CreateInvoicePage, { showAlert }) },
            { path: "viewinvoices",      Component: () => guarded("invoices",   InvoicesPage,     { showAlert }) },
            { path: "ledgerpage",        Component: () => guarded("ledger",     LedgerRoutePage,  { showAlert }) },
            { path: "expenses",          Component: () => guarded("expenses",   ExpensesPage,     { showAlert }) },
            { path: "managestock",       Component: () => guarded("stock",      StockPage,        { showAlert }) },
            { path: "reports",           Component: () => guarded("reports",    ReportsPage,      { showAlert }) },
            { path: "importproducts",    Component: () => guarded("stock",      ImportPage,       { showAlert }) },
            { path: "tdsreceivable",     Component: () => guarded("tds",        TDSPage,          { showAlert }) },
            { path: "gst-reports",       Component: () => guarded("gst",        GSTReportsPage,   { showAlert }) },
            { path: "purchase-orders",   Component: () => guarded("purchases",  POPage,           { showAlert }) },
            { path: "purchase-entry",    Component: () => guarded("purchases",  PurchaseEntryPage, { showAlert }) },
            { path: "managesuppliers",   Component: () => guarded("suppliers",  SuppliersPage,    { showAlert }) },
            { path: "financial-reports", Component: () => guarded("reports",    FinancialReportsPage, { showAlert }) },
            { path: "staff",             Component: () => guarded("staff",      StaffRoutePage,   { showAlert }) },
            { path: "staff/attendance",  Component: () => guarded("attendance", AttendanceRoutePage, { showAlert }) },
            { path: "staff/payroll",     Component: () => guarded("payroll",    PayrollRoutePage, { showAlert }) },
            { path: "bills",             Component: () => guarded("bills",      BillsRoutePage,   { showAlert }) },
            { path: "team",              Component: () => guarded("team",       TeamPage,         { showAlert }) },
            { path: "credit-notes",        Component: () => guarded("invoices",  CreditNotesPage,    { showAlert }) },
            { path: "debit-notes",         Component: () => guarded("purchases", DebitNotesPage,     { showAlert }) },
            { path: "recurring-invoices",  Component: () => guarded("invoices",  RecurringPage,      { showAlert }) },
            { path: "supplier-ledger",     Component: () => guarded("suppliers", SupplierLedgerPage, { showAlert }) },

            // Stock sub-routes
            { path: "products/:id/edit",    Component: () => guarded("stock", EditProductPage,  { showAlert }) },
            { path: "products/:id/adjust",  Component: () => guarded("stock", StockAdjustPage,  { showAlert }) },
            { path: "products/:id/history", Component: () => guarded("stock", StockHistoryPage, { showAlert }) },

            // Sales sub-routes
            { path: "sales/salespeople",      Component: () => guarded("sales", SalespeoplePage,        { showAlert }) },
            { path: "sales/salesperson/:id",  Component: () => guarded("sales", SalespersonProfilePage, { showAlert }) },
            { path: "sales/commissions",      Component: () => guarded("sales", CommissionsPage,        { showAlert }) },
          ],
        },
      ],
    },
  ]), [showAlert]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <UserState>
        <BankState>
          <ClientState>
            <InvoiceState>
              <RouterProvider router={myRouter} />
              <V2Toast alert={alert} />
            </InvoiceState>
          </ClientState>
        </BankState>
      </UserState>
    </ThemeProvider>
  );
}

export default App;